import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const PRIO_COLORS = { urgente: "bg-red-500", haute: "bg-amber-500", normale: "bg-slate-400", basse: "bg-slate-300" };

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [form, setForm] = useState({ titre: "", echeance: "", priorite: "normale" });
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/tasks").then((r) => setTasks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (params.get("new")) {
      setForm((f) => ({ ...f, titre: params.get("new") === "visite" ? "Visite : " : "" }));
      setParams({}, { replace: true });
      document.getElementById("task-titre")?.focus();
    }
  }, [params, setParams]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.titre.trim()) return;
    try {
      await api.post("/tasks", form);
      setForm({ titre: "", echeance: "", priorite: "normale" });
      load();
    } catch (err) { toast.error(fmtError(err)); }
  };

  const toggle = async (t) => {
    await api.put(`/tasks/${t.id}`, { ...t, fait: !t.fait }).catch(() => {});
    load();
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="tasks-page">
      <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight">Tâches</h1>
      <form onSubmit={add} className="bg-card rounded-xl border border-border p-4 flex flex-col sm:flex-row gap-2" data-testid="task-form">
        <Input id="task-titre" data-testid="task-titre" placeholder="Nouvelle tâche…" value={form.titre}
          onChange={(e) => setForm({ ...form, titre: e.target.value })} className="flex-1 py-3.5" />
        <Input data-testid="task-echeance" type="date" value={form.echeance} onChange={(e) => setForm({ ...form, echeance: e.target.value })} className="sm:w-44" />
        <select data-testid="task-priorite" value={form.priorite} onChange={(e) => setForm({ ...form, priorite: e.target.value })}
          className="h-12 rounded-lg border border-input bg-background px-3 text-sm font-semibold">
          <option value="basse">Basse</option><option value="normale">Normale</option><option value="haute">Haute</option><option value="urgente">Urgente</option>
        </select>
        <Button data-testid="task-add" type="submit" className="h-12 bg-amber-500 text-slate-900 font-bold hover:bg-amber-400"><Plus className="h-5 w-5" /></Button>
      </form>
      <div className="space-y-2" data-testid="tasks-list">
        {tasks.map((t) => (
          <div key={t.id} className={`bg-card rounded-xl border p-4 flex items-center gap-3 ${t.fait ? "opacity-50 border-border" : "border-border"}`} data-testid={`task-${t.id}`}>
            <input type="checkbox" data-testid={`task-toggle-${t.id}`} checked={t.fait} onChange={() => toggle(t)} className="h-6 w-6 accent-amber-500" />
            <div className="flex-1">
              <div className={`font-semibold text-sm ${t.fait ? "line-through" : ""}`}>{t.titre}</div>
              {t.echeance && <div className="text-xs text-muted-foreground font-mono-num">{t.echeance}</div>}
            </div>
            <span className={`${PRIO_COLORS[t.priorite] || "bg-slate-400"} text-white text-[10px] font-bold px-2 py-1 rounded uppercase`}>{t.priorite}</span>
            <button data-testid={`task-delete-${t.id}`} onClick={async () => { await api.delete(`/tasks/${t.id}`).catch(() => {}); load(); }}
              className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {tasks.length === 0 && <p className="text-sm text-muted-foreground" data-testid="tasks-empty">Aucune tâche.</p>}
      </div>
    </div>
  );
}
